/**
 * DropdownDiagOverlay (PRICING-SELECT-FLICKER-001) — TEMPORARY diagnostic.
 * Rendered only when the page URL contains ?dropdown_diag=1.
 * Captures, live from the affected machine while the user reproduces the
 * flicker: browser/DPI environment, trigger & list rects, popper transform,
 * open-state flips, list rect jumps, scroll oscillation, focus moves and
 * animation restarts. "Copy Report" puts a JSON payload on the clipboard.
 * Remove after diagnosis is complete.
 */
import React, { useEffect, useRef, useState } from 'react';

const box = (r) => r ? { x: +r.x.toFixed(2), y: +r.y.toFixed(2), w: +r.width.toFixed(2), h: +r.height.toFixed(2) } : null;

export default function DropdownDiagOverlay() {
  const [snap, setSnap] = useState({});
  const [copied, setCopied] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const counters = useRef({
    openFlips: 0, rectJumps: 0, maxJumpPx: 0, scrollFlaps: 0,
    focusMoves: 0, animStarts: 0, frames: 0, subpixelFrames: 0,
  });
  const prev = useRef({ open: false, rect: null, scrollTop: null, scrollDir: 0, focusId: null });
  const report = useRef(null);

  useEffect(() => {
    const onAnim = () => { counters.current.animStarts += 1; };
    document.addEventListener('animationstart', onAnim, true);
    let raf;
    const loop = () => {
      const c = counters.current;
      c.frames += 1;
      const list = document.querySelector('[role="listbox"]');
      const trigger = document.querySelector('[data-testid="product-select"]');
      const wrapper = list?.closest('[data-radix-popper-content-wrapper]');
      const viewport = list?.querySelector('[data-radix-select-viewport]') || list;
      const open = !!list;
      if (open !== prev.current.open) { c.openFlips += 1; prev.current.open = open; prev.current.rect = null; prev.current.scrollTop = null; }
      let live = {};
      if (open) {
        const r = list.getBoundingClientRect();
        if (prev.current.rect) {
          const jump = Math.max(Math.abs(r.x - prev.current.rect.x), Math.abs(r.y - prev.current.rect.y));
          if (jump > 0.25) { c.rectJumps += 1; c.maxJumpPx = Math.max(c.maxJumpPx, +jump.toFixed(2)); }
        }
        prev.current.rect = r;
        const st = viewport?.scrollTop ?? 0;
        if (prev.current.scrollTop !== null) {
          const dir = Math.sign(st - prev.current.scrollTop);
          if (dir !== 0 && prev.current.scrollDir !== 0 && dir !== prev.current.scrollDir) c.scrollFlaps += 1;
          if (dir !== 0) prev.current.scrollDir = dir;
        }
        prev.current.scrollTop = st;
        const focusId = document.activeElement?.id || trigger?.getAttribute('aria-activedescendant') || null;
        if (focusId !== prev.current.focusId) { if (prev.current.focusId !== null) c.focusMoves += 1; prev.current.focusId = focusId; }
        const cs = wrapper ? getComputedStyle(wrapper) : null;
        const tf = cs?.transform || 'none';
        const m = tf.match(/matrix\(([^)]+)\)/);
        const tx = m ? parseFloat(m[1].split(',')[4]) : null;
        const ty = m ? parseFloat(m[1].split(',')[5]) : null;
        if (tx !== null && (tx % 1 !== 0 || ty % 1 !== 0)) c.subpixelFrames += 1;
        live = {
          listRect: box(r),
          triggerRect: box(trigger?.getBoundingClientRect()),
          popperTransform: tf === 'none' ? 'none' : `translate(${tx}, ${ty})`,
          popperOpacity: cs?.opacity,
          insideDialog: !!list.closest('[role="dialog"]'),
          scrollTop: st,
          scrollHeight: viewport?.scrollHeight,
          clientHeight: viewport?.clientHeight,
        };
      }
      const data = {
        env: {
          userAgent: navigator.userAgent,
          devicePixelRatio: window.devicePixelRatio,
          zoomEstimate: +(window.outerWidth / window.innerWidth).toFixed(3),
          visualViewportScale: window.visualViewport?.scale,
          screen: `${window.screen.width}x${window.screen.height}`,
          inner: `${window.innerWidth}x${window.innerHeight}`,
          hardwareConcurrency: navigator.hardwareConcurrency,
        },
        counters: { ...c },
        live,
        open,
        capturedAt: new Date().toISOString(),
      };
      report.current = data;
      if (c.frames % 6 === 0) setSnap(data);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('animationstart', onAnim, true); };
  }, []);

  const copy = async () => {
    const text = JSON.stringify(report.current, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setShowRaw(true); // clipboard blocked — show selectable JSON instead
    }
  };

  const download = () => {
    const blob = new Blob([JSON.stringify(report.current, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dropdown-diag-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const c = snap.counters || {};
  return (
    <div
      className="fixed bottom-3 left-3 z-[9999] w-[340px] rounded-lg border border-amber-300 bg-amber-50/95 shadow-xl p-3 text-[11px] font-mono text-slate-800"
      data-testid="dropdown-diag-overlay"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-amber-800">DROPDOWN DIAG (temp)</span>
        <span className="flex gap-1">
          <button onClick={copy} className="px-2 py-0.5 rounded bg-amber-600 text-white font-sans font-semibold" data-testid="diag-copy-report">
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
          <button onClick={download} className="px-2 py-0.5 rounded bg-slate-600 text-white font-sans font-semibold" data-testid="diag-download-report">
            Download
          </button>
          <button onClick={() => setShowRaw((v) => !v)} className="px-2 py-0.5 rounded bg-slate-400 text-white font-sans font-semibold" data-testid="diag-show-raw">
            {showRaw ? 'Hide' : 'Show'}
          </button>
        </span>
      </div>
      {showRaw && (
        <textarea
          readOnly
          value={JSON.stringify(report.current, null, 2)}
          onFocus={(e) => e.target.select()}
          className="w-full h-40 mb-1 p-1 text-[10px] border border-amber-300 rounded bg-white"
          data-testid="diag-raw-json"
        />
      )}
      <div>DPR {snap.env?.devicePixelRatio} · zoom≈{snap.env?.zoomEstimate} · vvScale {snap.env?.visualViewportScale}</div>
      <div>openFlips <b>{c.openFlips}</b> · rectJumps <b>{c.rectJumps}</b> (max {c.maxJumpPx}px)</div>
      <div>scrollFlaps <b>{c.scrollFlaps}</b> · focusMoves {c.focusMoves} · anims {c.animStarts}</div>
      <div>subpixelFrames {c.subpixelFrames}/{c.frames} · listOpen: {String(snap.open)}</div>
      {snap.live?.listRect && (
        <div className="text-slate-500">list y {snap.live.listRect.y} h {snap.live.listRect.h} · scrollTop {snap.live.scrollTop}/{snap.live.scrollHeight}</div>
      )}
      <div className="mt-1 text-[10px] text-amber-700 font-sans">Open the Product dropdown, reproduce the flicker for ~10s, then Copy Report and paste it in chat.</div>
    </div>
  );
}
